import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Clock, MapPin, Phone, Mail, FileText, Receipt, MessageSquare, FolderOpen,
  Loader2, CheckCircle2, AlertCircle, Calendar
} from "lucide-react";
import OfflineBadge from "@/components/OfflineBadge";

export default function Aujourdhui() {
  const [data, setData] = useState(null);
  const [noProfile, setNoProfile] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/salarie/aujourdhui");
      setData(data);
      setNoProfile(false);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 404) {
        setNoProfile(true);
      } else if (st === 403) {
        // Rôle non destiné à cet espace (employeur, comptable...) : état informatif, pas d'erreur.
        setForbidden(true);
      } else {
        toast.error("Impossible de charger votre journée");
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex justify-center py-20" data-testid="aujourdhui-page"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>;
  }
  if (forbidden) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center space-y-3" data-testid="aujourdhui-page">
        <AlertCircle className="h-10 w-10 mx-auto text-[#C5A059]" />
        <h1 className="text-lg font-semibold text-slate-900" data-testid="aujourdhui-forbidden-title">Espace réservé aux salariés du groupement</h1>
        <p className="text-sm text-slate-500" data-testid="aujourdhui-forbidden-text">
          Votre rôle ({user?.role || "utilisateur"}) ne donne pas accès à l'espace « Aujourd'hui ».
          Vos écrans de pilotage sont disponibles depuis le menu.
        </p>
        <Button variant="outline" onClick={() => nav("/app")} data-testid="aujourdhui-back-dashboard-btn">
          Retour au tableau de bord
        </Button>
      </div>
    );
  }
  if (noProfile) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center space-y-3" data-testid="aujourdhui-page">
        <AlertCircle className="h-10 w-10 mx-auto text-[#C5A059]" />
        <h1 className="text-lg font-semibold text-slate-900" data-testid="aujourdhui-no-profile-title">Aucun dossier salarié lié à ce compte</h1>
        <p className="text-sm text-slate-500" data-testid="aujourdhui-no-profile-text">
          L'espace « Aujourd'hui » présente les missions du jour d'un salarié du groupement.
          Ce compte ({user?.email || "utilisateur"}) n'est rattaché à aucun dossier salarié.
          Contactez votre gestionnaire si vous pensez qu'il s'agit d'une erreur de rattachement.
        </p>
        <Button variant="outline" onClick={() => nav("/app")} data-testid="aujourdhui-back-dashboard-btn">
          Retour au tableau de bord
        </Button>
      </div>
    );
  }
  if (!data) return <div className="p-6 text-center text-slate-500" data-testid="aujourdhui-page">Pas de données</div>;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Bonjour";
    if (h < 18) return "Bon après-midi";
    return "Bonsoir";
  })();

  return (
    <div className="max-w-2xl mx-auto px-3 py-4 space-y-5 sm:py-6" data-testid="aujourdhui-page">
      {/* En-tête */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#C5A059]">Aujourd'hui</div>
          <h1 className="text-2xl font-semibold text-slate-900 leading-tight mt-0.5">
            {greeting}, {data.salarie?.prenom || ""}
          </h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(data.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <OfflineBadge />
      </div>

      {/* Missions du jour */}
      {data.pas_de_mission_aujourdhui ? (
        <Card className="p-5 bg-amber-50 border-amber-200" data-testid="no-mission-today">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-amber-900">Aucune mission prévue aujourd'hui</div>
              <div className="text-sm text-amber-700 mt-0.5">
                Vous pouvez tout de même saisir vos heures ou vos frais si vous êtes intervenu(e).
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.missions_du_jour.map((m) => (
            <Card key={m.id} className="p-4 border-l-4 border-l-[#C5A059]" data-testid={`mission-today-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{m.libelle}</div>
                  <div className="text-sm text-slate-600 flex items-center gap-1.5 mt-1.5">
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <span className="truncate">{m.entreprise?.nom} — {m.entreprise?.lieu || "lieu non précisé"}</span>
                  </div>
                  {(m.horaires_prevus?.debut || m.horaires_prevus?.fin) && (
                    <div className="text-sm text-slate-600 flex items-center gap-1.5 mt-1">
                      <Clock className="h-3.5 w-3.5 text-slate-400" />
                      {m.horaires_prevus.debut} — {m.horaires_prevus.fin}
                    </div>
                  )}
                </div>
              </div>
              {m.responsable?.email && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3 text-xs">
                  <Badge variant="outline" className="font-normal">Contact</Badge>
                  <a href={`mailto:${m.responsable.email}`} className="flex items-center gap-1.5 text-slate-700 hover:text-[#C5A059]" data-testid={`mission-contact-${m.id}`}>
                    <Mail className="h-3 w-3" />
                    {m.responsable.nom || m.responsable.email}
                  </a>
                </div>
              )}
              {m.consignes && (
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 italic">
                  « {m.consignes.slice(0, 180)}{m.consignes.length > 180 ? "…" : ""} »
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* 4 accès rapides en 2 taps */}
      <div className="grid grid-cols-2 gap-3">
        <QuickAccess
          icon={Clock}
          label="Mes heures"
          sub={`${data.semaine.heures_total}h cette semaine`}
          testid="quick-heures"
          onClick={() => nav("/app/timesheets")}
        />
        <QuickAccess
          icon={Receipt}
          label="Mes frais"
          sub={`${data.compteurs_rapides.frais_en_cours} en cours`}
          testid="quick-frais"
          onClick={() => nav("/app/frais")}
        />
        <QuickAccess
          icon={MessageSquare}
          label="Mes demandes"
          sub={`${data.compteurs_rapides.demandes_ouvertes} ouvertes`}
          testid="quick-demandes"
          onClick={() => nav("/app/demandes")}
        />
        <QuickAccess
          icon={FolderOpen}
          label="Mes documents"
          sub={`${data.compteurs_rapides.documents_personnels} documents`}
          testid="quick-documents"
          onClick={() => nav("/app/ged")}
        />
      </div>

      {/* Résumé semaine : statuts feuilles */}
      <Card className="p-4" data-testid="semaine-summary">
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium text-slate-900">Ma semaine</div>
          <span className="text-xs text-slate-500">{data.semaine.debut} → {data.semaine.fin}</span>
        </div>
        <div className="grid grid-cols-4 gap-2 text-center">
          <StatusChip label="Saisies" count={data.semaine.statuts.saisie || 0} color="slate" />
          <StatusChip label="Validées" count={data.semaine.statuts.validee || 0} color="blue" />
          <StatusChip label="Signées" count={data.semaine.statuts.signee || 0} color="green" />
          <StatusChip label="Contestées" count={data.semaine.statuts.contestee || 0} color="red" />
        </div>
      </Card>

      {/* Documents utiles */}
      {data.documents_utiles?.length > 0 && (
        <Card className="p-4">
          <div className="font-medium text-slate-900 mb-2">Documents utiles</div>
          <div className="space-y-1.5">
            {data.documents_utiles.map((d) => (
              <div key={d.id} className="flex items-center gap-2 text-sm text-slate-700 py-1">
                <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="truncate">{d.titre}</span>
                <Badge variant="outline" className="text-xs font-normal shrink-0">{d.categorie}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function QuickAccess({ icon: Icon, label, sub, testid, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group p-4 rounded-xl bg-white border border-slate-200 hover:border-[#C5A059] hover:shadow-md transition active:scale-[0.98] text-left"
    >
      <Icon className="h-6 w-6 text-[#C5A059] mb-2" />
      <div className="font-medium text-slate-900 text-sm">{label}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </button>
  );
}

function StatusChip({ label, count, color }) {
  const colors = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div className={`rounded-lg border px-2 py-2 ${colors[color]}`} data-testid={`status-chip-${color}`}>
      <div className="text-lg font-semibold leading-none">{count}</div>
      <div className="text-[10px] uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}
